// @flow

import React from 'react'
import styled, { css } from 'styled-components'
import { Menu, Dropdown, Input } from 'antd'
import { mediaMin } from 'frontend_core/dist/styles/media'
import {
  fontSize,
  spacing,
  borderRadius,
} from 'frontend_core/dist/styles/variables'
import OptimizedImage from 'components/atoms/OptimizedImage'
import Colors from 'components/styles/colors'
import Icon from 'components/atoms/Icon'
import ReputationStar from 'components/shared/ReputationStar'
import Button from 'components/atoms/Button'
import LinkText from 'components/atoms/LinkText'
import { type Favorite } from 'types/favorite'
import Path from 'routes/path'
import { imagePath } from 'frontend_core/dist/helpers/image'

const CardSpaceWithMemoUI = styled.li`
  display: block;
  width: 100%;
  padding: ${spacing.medium}px 0;
  border-top: 1px solid ${Colors.grayD};

  &:last-child {
    border-bottom: 1px solid ${Colors.grayD};
  }

  ${mediaMin.phone`
    padding: ${spacing.medium_2}px 0 ${spacing.medium}px;
  `};
`

const Container = styled.div`
  position: relative;
  ${mediaMin.phone`
    position: static;
  `};
`

const Heading = styled.div`
  display: block;
  ${mediaMin.phone`
    display: table;
    table-layout: fixed;
    width: 100%;
  `};
`

const Head = styled.div`
  position: relative;
  padding-top: 66.666%;
  border-radius: ${borderRadius}px;
  overflow: hidden;
  ${mediaMin.phone`
    display: table-cell;
    width: 195px;
    height: 130px;
    padding-top: 0;
  `};
`

const Body = styled.div`
  display: block;
  padding-top: ${spacing.small_3}px;
  ${mediaMin.phone`
    display: table-cell;
    padding: 0 0 0 ${spacing.medium}px;
  `};
`

const Title = styled.h2`
  color: ${Colors.black};
  font-size: ${fontSize.small_3}px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${mediaMin.phone`
    font-size: ${fontSize.medium}px;
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
  `};
`

const Time = styled.p`
  margin-top: ${spacing.small_2}px;
  color: ${Colors.black};
  font-size: ${fontSize.medium}px;
  font-weight: bold;
  line-height: 1;
  ${mediaMin.phone`
    font-size: ${fontSize.large}px;
  `};
`

const TimeUnit = styled.span`
  display: inline-block;
  font-size: ${fontSize.small__25}px;
  font-weight: normal;
  ${mediaMin.phone`
    font-size: ${fontSize.small_3}px;
  `};
`

const DetailWrapper = styled.div`
  margin-top: ${spacing.small_2}px;
  font-size: ${fontSize.small}px;
  font-weight: normal;
  ${mediaMin.phone`
    display: table;
    width: 100%;
    margin-top: ${spacing.small_3}px;
    font-size: ${fontSize.small__25}px;
  `};
`

const Detail = styled.div`
  ${mediaMin.phone`
    display: table-cell;
    max-width: 1px;
  `};
`

const DetailText = styled.span`
  display: inline-block;
  color: ${Colors.black};

  &:not(:first-child) {
    margin-left: ${spacing.small_2}px;
  }
`

const DetailPlace = styled.p`
  margin-top: ${spacing.small_2}px;
  color: ${Colors.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const IconWrapper = styled.span`
  display: inline-block;
  margin-left: ${spacing.tiny}px;
`

const DetailButton = styled.div`
  position: absolute;
  top: 112px;
  right: 8px;
  ${mediaMin.phone`
    position: static;
    display: table-cell;
    width: 32px;
    vertical-align: bottom;
  `};
`

const DetailButtonWrapper = styled.div`
  position: relative;
`

const DetailButtonMenuItem = styled.a`
  display: block;
  padding: ${spacing.small_3}px ${spacing.small_3}px;
  color: ${Colors.black};
  font-size: ${fontSize.small_3}px;
  cursor: pointer;
`

const ReputationNum = styled.span`
  display: inline-block;
  margin-left: ${spacing.tiny}px;
`

const MemoContainer = styled.div`
  margin-top: ${spacing.medium}px;
  padding-top: ${spacing.small_3}px;
  border-top: 1px dashed ${Colors.grayE};
  ${mediaMin.phone`
    margin-top: ${spacing.medium_2}px;
  `};
`

const MemoContainerHeading = styled.div`
  &::after {
    content: '';
    clear: both;
    display: block;
  }
`

const MemoGuideText = styled.p`
  float: left;
  color: ${Colors.grayD};
  font-size: ${fontSize.small_3}px;
`

const ButtonWrapper = styled.div`
  margin-top: ${spacing.small_3}px;
  text-align: right;
`

const TextAreaWrapper = styled.div`
  margin-top: ${spacing.medium}px;
`

const LinkTextWrapper = styled.div`
  float: right;
`

const MemoTextWrapper = styled.div`
  display: table;
  width: 100%;
`

const MemoText = styled.p`
  display: table-cell;
  max-width: 1px;
  color: ${Colors.black};
  font-size: ${fontSize.small_3}px;
  line-height: 1.6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  ${({ isOpen }) =>
    isOpen &&
    css`
      overflow: visible;
      text-overflow: clip;
      white-space: normal;
    `};
`

const MemoTextLink = styled.div`
  display: table-cell;
  width: 84px;
  text-align: right;
  ${({ isOpenWritableMemo }) =>
    isOpenWritableMemo &&
    css`
      display: none;
    `};
`

const MemoTextIsOpenLink = styled.div`
  text-align: right;
`

const WritableSpace = styled.div`
  display: none;
  ${({ isOpenWritableMemo }) =>
    isOpenWritableMemo &&
    css`
      display: block;
    `};
`

export type FavoriteCardState = {
  isOpen?: boolean,
  isOpenWritableMemo?: boolean,
  memo?: string,
}

export type PropTypes = {
  favoriteData: Favorite,
  favoriteCardState: FavoriteCardState,
  onClickSaveMemo: () => void,
  onChangeMemo: (e: SyntheticInputEvent<*>) => void,
  toggleMemoText: () => void,
  toggleWritableMemo: () => void,
  onClickOpenSelectFavoriteListItemModal: Function,
  onClickOpenDeleteFavoriteListItemModal: Function,
}

export default function FavoriteCardSpaceMemo(props: PropTypes) {
  const {
    onClickSaveMemo,
    onChangeMemo,
    toggleMemoText,
    toggleWritableMemo,
    onClickOpenSelectFavoriteListItemModal,
    onClickOpenDeleteFavoriteListItemModal,
  } = props
  const {
    id,
    uid,
    thumbnails,
    space,
    min_price_text,
    max_price_text,
    min_price_unit_text,
    capacity,
    reputation_score,
    reputation_count,
    memo,
  } = props.favoriteData
  const { isOpen, isOpenWritableMemo, memo: editMemo } = props.favoriteCardState
  const roomUrl = Path.room(space.username, uid)
  return (
    <CardSpaceWithMemoUI>
      <Container>
        <Heading>
          <Head>
            <a href={roomUrl} target="_blank" rel="noopener noreferrer">
              <OptimizedImage
                src={
                  (Array.isArray(thumbnails) &&
                    thumbnails.length > 0 &&
                    thumbnails[0] &&
                    thumbnails[0].image) ||
                  imagePath('common/noimage_search.png')
                }
                alt={space.name}
                type="cover"
              />
            </a>
          </Head>
          <Body>
            <a href={roomUrl} target="_blank" rel="noopener noreferrer">
              <Title>{space.name}</Title>
            </a>
            <Time>
              {min_price_text}〜{max_price_text}
              <TimeUnit>/{min_price_unit_text}</TimeUnit>
              <IconWrapper>
                <Icon
                  className="fa fa-bolt"
                  color={Colors.yellow}
                  fontSizeSp={fontSize.medium}
                  fontSizePc={fontSize.large}
                  padding="0 3px"
                />
                <Icon
                  className="icon-spm-top_host"
                  color={Colors.yellow}
                  fontSizeSp={fontSize.medium}
                  fontSizePc={fontSize.large}
                />
                <Icon
                  className="icon-spm-discount"
                  color={Colors.red}
                  fontSizeSp={fontSize.small_3}
                  fontSizePc={fontSize.medium_2}
                />
              </IconWrapper>
            </Time>
            <DetailWrapper>
              <Detail>
                <DetailText>
                  <Icon
                    className="fa fa-user"
                    color={Colors.grayD}
                    positionType="left"
                  />
                  {i18n.t('page.room.capacity', { capacity })}
                </DetailText>
                <DetailText>
                  {space.stateText}
                  {space.city}
                </DetailText>
                <DetailText>
                  <ReputationStar score={reputation_score} />
                  <ReputationNum>
                    {i18n.t('page.room.numberOf', { count: reputation_count })}
                  </ReputationNum>
                </DetailText>
                <DetailPlace>{space.access}</DetailPlace>
              </Detail>
              <DetailButton>
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item>
                        <DetailButtonMenuItem
                          onClick={() => {
                            onClickOpenSelectFavoriteListItemModal(id)
                          }}
                        >
                          {i18n.t('favorite.chooseList')}
                        </DetailButtonMenuItem>
                      </Menu.Item>
                      <Menu.Item>
                        <DetailButtonMenuItem
                          onClick={() => {
                            onClickOpenDeleteFavoriteListItemModal(id)
                          }}
                        >
                          {i18n.t('page.shared.delete')}
                        </DetailButtonMenuItem>
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={['click']}
                >
                  <DetailButtonWrapper>
                    <Button
                      tag="button"
                      buttonType="onlyIconSmall"
                      colorType="blue"
                      isOnlyBorder
                    >
                      <Icon className="fa fa-ellipsis-h" />
                    </Button>
                  </DetailButtonWrapper>
                </Dropdown>
              </DetailButton>
            </DetailWrapper>
          </Body>
        </Heading>
        {memo ? (
          <div>
            <MemoContainer>
              <MemoTextWrapper>
                {isOpenWritableMemo ? (
                  <WritableSpace isOpenWritableMemo={isOpenWritableMemo}>
                    <TextAreaWrapper>
                      <Input.TextArea
                        value={editMemo}
                        rows={4}
                        placeholder={i18n.t(
                          'page.dashboard.favorite.writeMemo',
                        )}
                        onChange={onChangeMemo}
                      />
                    </TextAreaWrapper>
                    <ButtonWrapper>
                      <Button
                        tag="button"
                        buttonType="small"
                        colorType="gray"
                        buttonWidthPc={120}
                        buttonWidthSp={140}
                        onClick={toggleWritableMemo}
                      >
                        {i18n.t('page.search.cancel')}
                      </Button>
                      <Button
                        tag="button"
                        buttonType="small"
                        colorType="blue"
                        buttonWidthPc={120}
                        buttonWidthSp={140}
                        buttonMarginSp={`0 0 0 ${spacing.small_2}px`}
                        disabled={!editMemo}
                        onClick={onClickSaveMemo}
                      >
                        {i18n.t('page.shared.l_00033')}
                      </Button>
                    </ButtonWrapper>
                  </WritableSpace>
                ) : (
                  <MemoText isOpen={isOpen} onClick={toggleWritableMemo}>
                    {memo}
                    <Icon
                      className="fa fa-pencil"
                      color={Colors.blue}
                      positionType="right"
                    />
                  </MemoText>
                )}
                {isOpen || (
                  <MemoTextLink isOpenWritableMemo={isOpenWritableMemo}>
                    <LinkText onClick={toggleMemoText}>
                      {i18n.t('page.shared.l_00002')}
                      <Icon
                        className={'fa fa-angle-down'}
                        positionType="right"
                      />
                    </LinkText>
                  </MemoTextLink>
                )}
              </MemoTextWrapper>
            </MemoContainer>
            {!isOpenWritableMemo &&
              isOpen && (
                <MemoTextIsOpenLink>
                  <LinkText
                    fontSizeSp={fontSize.small_3}
                    onClick={toggleMemoText}
                  >
                    {i18n.t('page.search.close')}
                    <Icon className={'fa fa-angle-up'} positionType="right" />
                  </LinkText>
                </MemoTextIsOpenLink>
              )}
          </div>
        ) : (
          <MemoContainer>
            <MemoContainerHeading>
              <MemoGuideText>
                {i18n.t('page.dashboard.favorite.noneMemo')}
              </MemoGuideText>
              <LinkTextWrapper>
                <LinkText
                  fontSizeSp={fontSize.small_3}
                  onClick={toggleWritableMemo}
                >
                  {i18n.t('page.dashboard.favorite.writeMemo')}
                </LinkText>
              </LinkTextWrapper>
            </MemoContainerHeading>
            {isOpenWritableMemo && (
              <WritableSpace isOpenWritableMemo={isOpenWritableMemo}>
                <TextAreaWrapper>
                  <Input.TextArea
                    value={editMemo}
                    rows={4}
                    placeholder={i18n.t('page.dashboard.favorite.writeMemo')}
                    onChange={onChangeMemo}
                  />
                </TextAreaWrapper>
                <ButtonWrapper>
                  <Button
                    tag="button"
                    buttonType="small"
                    colorType="gray"
                    buttonWidthPc={120}
                    buttonWidthSp={140}
                    onClick={toggleWritableMemo}
                  >
                    {i18n.t('page.search.cancel')}
                  </Button>
                  <Button
                    tag="button"
                    buttonType="small"
                    colorType="blue"
                    buttonWidthPc={120}
                    buttonWidthSp={140}
                    buttonMarginSp={`0 0 0 ${spacing.small_2}px`}
                    disabled={!editMemo}
                    onClick={onClickSaveMemo}
                  >
                    {i18n.t('page.shared.l_00033')}
                  </Button>
                </ButtonWrapper>
              </WritableSpace>
            )}
          </MemoContainer>
        )}
      </Container>
    </CardSpaceWithMemoUI>
  )
}



// WEBPACK FOOTER //
// ./src/components/molecules/CardSpaceWithMemo/index.jsx